import { useRef, useCallback } from "react";

// Lightweight Web Audio SFX generator — no external assets needed.
export function useSound(enabledRef) {
  const ctxRef = useRef(null);

  const ctx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  }, []);

  const tone = useCallback(
    (freq, dur, type = "square", vol = 0.15, slideTo = null) => {
      if (enabledRef && enabledRef.current === false) return;
      const ac = ctx();
      const t0 = ac.currentTime;
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, t0);
      if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
      gain.gain.setValueAtTime(vol, t0);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      osc.connect(gain).connect(ac.destination);
      osc.start(t0);
      osc.stop(t0 + dur);
    },
    [enabledRef, ctx]
  );

  const click = useCallback(() => tone(1100, 0.04, "square", 0.06), [tone]);
  const explode = useCallback(() => {
    tone(420, 0.18, "sawtooth", 0.16, 90);
    tone(220, 0.22, "square", 0.1, 60);
  }, [tone]);
  const miss = useCallback(() => {
    tone(320, 0.07, "square", 0.1, 260);
    setTimeout(() => tone(260, 0.07, "square", 0.08, 200), 70);
  }, [tone]);
  const bossHit = explode;
  const life = useCallback(() => {
    tone(440, 0.45, "triangle", 0.18, 110);
    setTimeout(() => tone(90, 0.3, "sawtooth", 0.14, 50), 120);
  }, [tone]);
  const rainbow = useCallback(() => {
    [880, 1175, 1480, 1760].forEach((f, i) => setTimeout(() => tone(f, 0.08, "square", 0.12), i * 50));
  }, [tone]);
  const missile = useCallback(() => tone(900, 0.25, "sawtooth", 0.08, 200), [tone]);
  const win = useCallback(() => {
    [523, 659, 784, 1047].forEach((f, i) =>
      setTimeout(() => tone(f, 0.16, "square", 0.15), i * 120)
    );
  }, [tone]);
  const lose = useCallback(() => {
    [400, 320, 240, 160].forEach((f, i) =>
      setTimeout(() => tone(f, 0.2, "sawtooth", 0.15), i * 140)
    );
  }, [tone]);

  return { click, explode, miss, bossHit, life, rainbow, missile, win, lose, resume: ctx };
}
